import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCenter,
  useDroppable,
  useDraggable,
  Active,
} from '@dnd-kit/core';

interface DraggableItem {
  id: string;
  questionOptionID: string;
  text: string;
}

interface SortableListProps {
  questionId: string;
  initialAvailableItems: DraggableItem[];
  initialDroppedItems?: (DraggableItem | null)[];
  onSortChange: (questionId: string, sortedItems: (DraggableItem | null)[]) => void;
  numberOfDropZones: number;
  title?: string;
  subtitle?: string;
}

const DRAG_HANDLE = '⠿';
const AVAILABLE_ITEMS_ID = 'available-items';
const DROP_ZONE_PREFIX = 'drop-zone-';
const DRAG_OVERLAY_OPACITY = 0.8;
const DRAGGING_OPACITY = 0.5;

// Detectar si es dispositivo móvil
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         ('ontouchstart' in window) ||
         (navigator.maxTouchPoints > 0);
};

const SENSOR_CONFIG = {
  POINTER: { 
    distance: isMobile() ? 10 : 8, // Mayor distancia en móviles
  },
  TOUCH: { 
    delay: 0, // Sin delay para mejor respuesta
    tolerance: isMobile() ? 10 : 5, // Mayor tolerancia en móviles
  },
};

// Componente básico para elementos draggables
const DraggableItem: React.FC<{ 
  item: DraggableItem; 
  className?: string;
}> = ({ item, className = "draggable-item" }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    data: { item },
  });

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? DRAGGING_OPACITY : 1,
  };

  // Prevenir comportamientos por defecto en móviles
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Permitir que @dnd-kit maneje el evento
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // Prevenir menú contextual en móviles
  }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={className}
      onTouchStart={handleTouchStart}
      onContextMenu={handleContextMenu}
      {...listeners}
      {...attributes}
    >
      <span className="drag-handle">{DRAG_HANDLE}</span>
      {item.text}
    </div>
  );
};

// Componente para zona de opciones disponibles
const AvailableItemsZone: React.FC<{ items: DraggableItem[] }> = ({ items }) => {
  const { setNodeRef } = useDroppable({ id: AVAILABLE_ITEMS_ID });

  return (
    <div ref={setNodeRef} className="draggable-options">
      {items.map((item) => (
        <DraggableItem key={item.id} item={item} />
      ))}
    </div>
  );
};

// Componente para zonas de drop
const DropZone: React.FC<{ 
  id: string; 
  index: number; 
  item: DraggableItem | null; 
  isDragOver: boolean;
}> = ({ id, index, item, isDragOver }) => {
  const { isOver, setNodeRef } = useDroppable({ id });
  const isHighlighted = isDragOver || isOver;

  return (
    <div className="drop-zone-container">
      <span className="drop-zone-number">{index + 1}.</span>
      <div
        ref={setNodeRef}
        className={`drop-zone ${isHighlighted ? 'drag-over' : ''}`}
      >
        {item ? (
          <DraggableItem item={item} className="draggable-item dropped" />
        ) : (
          <span className="drop-zone-placeholder" />
        )}
      </div>
    </div>
  );
};

const getActiveItem = (active: Active): DraggableItem | null => {
  return (active.data.current?.item as DraggableItem) || null;
};

const getZoneIndexFromId = (id: string): number => {
  return parseInt(id.replace(DROP_ZONE_PREFIX, ''), 10);
};

const isDropZone = (id: string): boolean => {
  return id.startsWith(DROP_ZONE_PREFIX);
};

const SortableList: React.FC<SortableListProps> = ({
  questionId,
  initialAvailableItems,
  initialDroppedItems,
  onSortChange,
  numberOfDropZones,
  title,
  subtitle,
}) => {
  const [droppedItems, setDroppedItems] = useState<(DraggableItem | null)[]>([]);
  const [activeItem, setActiveItem] = useState<DraggableItem | null>(null);
  const [dragOverZone, setDragOverZone] = useState<number | null>(null);

  const availableItems = useMemo(() => 
    initialAvailableItems.filter(
      (item) => !droppedItems.some((dropped) => dropped?.id === item.id)
    ), 
    [initialAvailableItems, droppedItems]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { 
      activationConstraint: SENSOR_CONFIG.POINTER 
    }),
    useSensor(TouchSensor, { 
      activationConstraint: SENSOR_CONFIG.TOUCH 
    }),
    useSensor(KeyboardSensor, {})
  );

  useEffect(() => {
    const initialItems = initialDroppedItems?.length === numberOfDropZones 
      ? initialDroppedItems 
      : Array(numberOfDropZones).fill(null);
    
    setDroppedItems(initialItems);
  }, [numberOfDropZones, initialDroppedItems]);

  useEffect(() => {
    if (droppedItems.length > 0) { 
      onSortChange(questionId, droppedItems);
    }
  }, [droppedItems, onSortChange, questionId]);

  const handleDropInZone = useCallback((targetZoneIndex: number) => {
    if (!activeItem) return;

    setDroppedItems(prevItems => {
      const newItems = [...prevItems];
      const currentIndex = newItems.findIndex(item => item?.id === activeItem.id);
      const targetZoneItem = newItems[targetZoneIndex];
      
      if (currentIndex !== -1 && targetZoneItem && targetZoneItem.id !== activeItem.id) {
        // Intercambio entre zonas ocupadas
        newItems[currentIndex] = targetZoneItem;
        newItems[targetZoneIndex] = activeItem;
      } else if (currentIndex !== -1 && !targetZoneItem) {
        // Mover de zona ocupada a zona vacía
        newItems[currentIndex] = null;
        newItems[targetZoneIndex] = activeItem;
      } else if (currentIndex === -1) {
        // Mover de disponibles a zona
        newItems[targetZoneIndex] = activeItem;
      }
      
      return newItems;
    });
  }, [activeItem]);

  const handleDropInAvailable = useCallback(() => {
    if (!activeItem) return;

    setDroppedItems(prevItems => 
      prevItems.map(item => item?.id === activeItem.id ? null : item)
    );
  }, [activeItem]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const draggedItem = getActiveItem(event.active);
    setActiveItem(draggedItem);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    
    if (over && isDropZone(over.id.toString())) {
      const zoneIndex = getZoneIndexFromId(over.id.toString());
      setDragOverZone(zoneIndex);
    } else {
      setDragOverZone(null);
    }
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { over } = event;
    
    if (!activeItem) {
      setDragOverZone(null);
      return;
    }

    if (over) {
      if (isDropZone(over.id.toString())) {
        const dropZoneIndex = getZoneIndexFromId(over.id.toString());
        handleDropInZone(dropZoneIndex);
      } else if (over.id === AVAILABLE_ITEMS_ID) {
        handleDropInAvailable();
      }
    } else {
      handleDropInAvailable();
    }

    setActiveItem(null);
    setDragOverZone(null);
  }, [activeItem, handleDropInZone, handleDropInAvailable]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="container">
        {title && <h1>{title}</h1>}
        {subtitle && <p>{subtitle}</p>}

        <AvailableItemsZone items={availableItems} />

        <div className="drop-zones">
          {droppedItems.map((item, index) => (
            <DropZone
              key={`${DROP_ZONE_PREFIX}${index}`}
              id={`${DROP_ZONE_PREFIX}${index}`}
              index={index}
              item={item}
              isDragOver={dragOverZone === index}
            />
          ))}
        </div>

        <DragOverlay>
          {activeItem && (
            <div 
              className="draggable-item" 
              style={{ 
                opacity: DRAG_OVERLAY_OPACITY,
                transform: 'rotate(5deg)', // Efecto visual de arrastre
                zIndex: 1000, // Asegurar que esté encima
                pointerEvents: 'none', // Evitar interferencias
                ...(isMobile() && {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)', // Sombra más pronunciada en móviles
                  fontSize: '0.9em', // Tamaño ligeramente mayor
                })
              }}
            >
              <span className="drag-handle">{DRAG_HANDLE}</span>
              {activeItem.text}
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};

export default SortableList; 